import { useState, useEffect, useRef } from "react";
import { apiFetch } from "../../../context/AuthContext";

// useProcessPayroll: state + business logic for ProcessPayrollModal
const useProcessPayroll = ({
  isOpen,
  onClose,
  selectedMonth,
  onProcessComplete,
  refreshData,
  fetchPayrollData,
  refreshDataForMonth,
  setSelectedMonth,
  showAlert,
  t,
  language,
  showAlertModal,
  hideAlertModal,
  selectedEmployees,
  setSelectedEmployees,
  payrollData,
  clientName,
}) => {
  const API_BASE_URL = process.env.REACT_APP_BASE_URL;

  // ── Single-company config (legacy / backward-compat) ───────────────────────
  const [companyConfig, setCompanyConfig] = useState(null);
  const [cutoffDay, setCutoffDay] = useState(null);

  // ── Multi-company support ───────────────────────────────────────────────────
  const [companies, setCompanies] = useState([]); // [{ id, name }]
  const [companyConfigsById, setCompanyConfigsById] = useState({}); // { [companyName]: configObj }
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companiesError, setCompaniesError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    if (!clientName) {
      setCompanies([]);
      setCompanyConfigsById({});
      setCompanyConfig(null);
      setCutoffDay(null);
      return;
    }

    let cancelled = false;
    setCompaniesLoading(true);
    setCompaniesError(null);

    const run = async () => {
      try {
        // ── Step 1: detect working companies for this client ──────────────────
        // ?autoCreate=true also makes the backend create default configs for
        // any company that doesn't have one yet. The response only contains
        // { companyName, employeeCount, hasConfiguration } per company though —
        // not the actual config fields (e.g. payrollStartDate).
        const detectRes = await apiFetch(
          `${API_BASE_URL}/api/company-config/${encodeURIComponent(clientName)}/detect-companies?autoCreate=true`,
        );
        const detectJson = await detectRes.json();

        if (!detectRes.ok || detectJson.success === false) {
          throw new Error(
            detectJson.message || `HTTP ${detectRes.status} while detecting companies`,
          );
        }

        const data = detectJson.data || {};

        // The backend returns companies under `data.companies` normally, or
        // under `data.detected` on the branch where autoCreate just finished
        // creating configs for previously-unconfigured companies.
        const rawList = Array.isArray(data.companies)
          ? data.companies
          : Array.isArray(data.detected)
            ? data.detected
            : [];

        const companyNames = [
          ...new Set(rawList.map((c) => c?.companyName).filter(Boolean)),
        ];

        if (companyNames.length === 0) {
          if (!cancelled) {
            setCompanies([]);
            setCompanyConfigsById({});
            setCompanyConfig(null);
            setCutoffDay(null);
          }
          return;
        }

        // ── Step 2: fetch the full config for each company in parallel ────────
        // This is the endpoint that actually returns payrollStartDate etc.
        const configResults = await Promise.all(
          companyNames.map(async (name) => {
            try {
              const res = await apiFetch(
                `${API_BASE_URL}/api/company-config/${encodeURIComponent(clientName)}/${encodeURIComponent(name)}`,
              );
              const json = await res.json();
              const config = json?.data ?? null;
              return {
                name,
                config: config
                  ? { ...config, companyName: config.companyName || name }
                  : { companyName: name },
              };
            } catch (err) {
              console.error(`⚠️ Failed to fetch config for ${name}:`, err.message);
              return { name, config: { companyName: name } };
            }
          }),
        );

        if (cancelled) return;

        const companyList = configResults.map((c) => ({ id: c.name, name: c.name }));
        const configsById = {};
        configResults.forEach((c) => {
          configsById[c.name] = c.config;
        });

        setCompanies(companyList);
        setCompanyConfigsById(configsById);

        // Backward-compat: point companyConfig/cutoffDay at the first company
        const first = configResults[0]?.config ?? null;
        setCompanyConfig(first);

        const startDay = Number(first?.payrollStartDate);
        if (startDay && startDay >= 1 && startDay <= 31) {
          const derived = startDay === 1 ? 31 : startDay - 1;
          setCutoffDay(derived);
          setFormData((prev) => ({
            ...prev,
            payrollPeriod: { ...prev.payrollPeriod, day: derived },
          }));
        } else {
          setCutoffDay(null);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("⚠️ Failed to fetch companies:", err.message);
          setCompaniesError(err.message);
          setCompanies([]);
          setCompanyConfigsById({});
        }
      } finally {
        if (!cancelled) setCompaniesLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [isOpen, clientName]);

  // ── Form state ─────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    // Legacy single-period shape (kept for backward compat)
    payrollPeriod: {
      month: selectedMonth?.month || new Date().getMonth() + 1,
      year: selectedMonth?.year || new Date().getFullYear(),
      day: null,
      endDate: undefined,
    },
    // Multi-company shape, used by StepSelectPeriod
    companyIds: [],
    periodsByCompany: {},
  });

  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  });

  const [previewData, setPreviewData] = useState(null);
  const [selectedAll, setSelectedAll] = useState(false);
  const [processingPayroll, setProcessingPayroll] = useState(false);
  const [groupByMode, setGroupByMode] = useState("department");
  const [processingStatus, setProcessingStatus] = useState("");

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleInputChange = (field, value) => {
    if (field === "payrollPeriod") {
      const prevPeriod = formData.payrollPeriod;
      const monthOrYearChanged =
        value.month !== prevPeriod.month || value.year !== prevPeriod.year;

      setFormData((prev) => ({
        ...prev,
        payrollPeriod: {
          ...prev.payrollPeriod,
          ...value,
          ...(monthOrYearChanged && { endDate: undefined }),
        },
      }));

      if (monthOrYearChanged) {
        setSelectedMonth({ month: value.month, year: value.year });
      }
      return;
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEmployeeSelection = (employee) => {
    setSelectedEmployees((prev) => {
      const isSelected = prev.some((emp) => emp.id === employee.id);
      return isSelected
        ? prev.filter((emp) => emp.id !== employee.id)
        : [...prev, employee];
    });
  };

  const handleSelectAll = () => {
    if (selectedAll) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees([...payrollData]);
    }
    setSelectedAll(!selectedAll);
  };

  const calculatePreview = () => {
    const preview = {
      totalEmployees: selectedEmployees.length,
      totalGross: selectedEmployees.reduce((sum, emp) => sum + emp.grossSalary, 0),
      totalDeductions: selectedEmployees.reduce((sum, emp) => sum + emp.totalDeductions, 0),
      totalNet: selectedEmployees.reduce((sum, emp) => sum + emp.netSalary, 0),
      taxTotal: selectedEmployees.reduce((sum, emp) => sum + emp.taxMonth, 0),
      insuranceTotal: selectedEmployees.reduce((sum, emp) => sum + emp.socialInsurance, 0),
      employees: selectedEmployees.map((emp) => ({
        name: emp.employeeName,
        gross: emp.grossSalary,
        deductions: emp.totalDeductions,
        net: emp.netSalary,
      })),
    };
    setPreviewData(preview);
  };

  // ─────────────────────────────────────────────────────────────────────────
  const resolveCycleDates = (period, configForCompany = companyConfig) => {
    const { month, year } = period;
    const payrollStartDate = Number(configForCompany?.payrollStartDate) || 1;

    // Mirror autoPayrollGenerator.js's computeConfigDates exactly:
    //   >= 15: cycle = (previous month, day N)  →  (this month, day N-1)
    //   <  15: cycle = (this month, day N)      →  (next month, day N-1)
    let cycleStartYear = year;
    let cycleStartMonth = month;
    let cycleEndYear = year;
    let cycleEndMonth = month;

    if (payrollStartDate >= 15) {
      cycleStartMonth = month - 1;
      if (cycleStartMonth < 1) { cycleStartMonth = 12; cycleStartYear -= 1; }
      // end stays in `month`/`year`
    } else {
      cycleEndMonth = month + 1;
      if (cycleEndMonth > 12) { cycleEndMonth = 1; cycleEndYear += 1; }
      // start stays in `month`/`year`
    }

    const cycleStartDate =
      `${cycleStartYear}-${String(cycleStartMonth).padStart(2, "0")}-${String(payrollStartDate).padStart(2, "0")}`;

    let cycleEndDate;
    if (period.endDate) {
      cycleEndDate = period.endDate;
    } else {
      // Use (payrollStartDate - 1) as the day offset into cycleEndMonth and let
      // the Date constructor roll day 0 back a month natively. This fixes
      // payrollStartDate === 1: it now resolves to the last day of the SAME
      // month the cycle started in, instead of the last day of the *next*
      // month (the bug — the old special case used `new Date(cycleEndYear,
      // cycleEndMonth, 0)`, which is the last day of cycleEndMonth itself,
      // one month too late).
      const endDateObj = new Date(cycleEndYear, cycleEndMonth - 1, payrollStartDate - 1);
      cycleEndDate =
        `${endDateObj.getFullYear()}-${String(endDateObj.getMonth() + 1).padStart(2, "0")}-${String(endDateObj.getDate()).padStart(2, "0")}`;
    }

    return { cycleStartDate, cycleEndDate };
  };

  const processCompanyPayroll = async ({ companyId, period, employeesForCompany, isPaid }) => {
    const configForCompany = companyConfigsById[companyId] ?? companyConfig;

    const syncYear = Number(period?.year);
    const syncMonth = Number(period?.month);

    if (!syncYear || !syncMonth) {
      throw new Error(language === "ar" ? "يجب تحديد فترة الرواتب قبل المعالجة" : "Payroll period is required before processing");
    }

    const empInfCodes = [
      ...new Set(
        employeesForCompany
          .map((employee) => String(employee.infinityCode || "").trim())
          .filter(Boolean),
      ),
    ];

    if (!empInfCodes.length) {
      throw new Error(
        `No employees selected for payroll processing${companyId ? ` (${companyId})` : ""}`,
      );
    }

    const { cycleStartDate, cycleEndDate } = resolveCycleDates(
      { ...period, month: syncMonth, year: syncYear },
      configForCompany,
    );

    console.log("🔄 Processing payroll via /process endpoint...", {
      companyId, year: syncYear, month: syncMonth, cycleStartDate, cycleEndDate,
      employees: empInfCodes.length,
    });

    const periodLabel =
      `${syncYear}/${String(syncMonth).padStart(2, "0")} ` +
      `(${cycleStartDate} ${language === "ar" ? "←" : "→"} ${cycleEndDate})`;
    setProcessingStatus(
      language === "ar"
        ? `جارٍ معالجة رواتب ${empInfCodes.length} موظف${companyId ? ` — ${companyId}` : ""} — ${periodLabel}...`
        : `Processing payroll for ${empInfCodes.length} employee(s)${companyId ? ` — ${companyId}` : ""} — ${periodLabel}...`,
    );

    const params = new URLSearchParams({
      year: String(syncYear),
      month: String(syncMonth),
      cycleStartDate,
      cycleEndDate,
      isPaid: String(isPaid),
      ...(companyId ? { companyName: companyId } : {}),
    });

    const response = await apiFetch(
      `${API_BASE_URL}/payrollX/process?${params.toString()}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ empInfCodes, isPaid }),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    return response.json();
  };

  const handleProcessPayroll = async (isPaid = false) => {
    setProcessingPayroll(true);
    setProcessingStatus(language === "ar" ? "جارٍ تجهيز معالجة الرواتب..." : "Preparing payroll processing...");

    try {
      const data = formDataRef.current;
      const hasMultiCompanySelection =
        Array.isArray(data?.companyIds) && data.companyIds.length > 0;

      let results = [];

      if (hasMultiCompanySelection) {
        for (const companyId of data.companyIds) {
          const period = data.periodsByCompany?.[companyId] || data.payrollPeriod;
          if (!period || !period.month || !period.year) {
            throw new Error(`Missing pay period for company: ${companyId}`);
          }

          const employeesForCompany = selectedEmployees.filter(
            (emp) => !emp.workingCompany || emp.workingCompany === companyId,
          );

          const result = await processCompanyPayroll({
            companyId, period, employeesForCompany, isPaid,
          });

          results.push({ companyId, result });
        }
      } else {
        const period = data?.payrollPeriod;
        const result = await processCompanyPayroll({
          companyId: null, period, employeesForCompany: selectedEmployees, isPaid,
        });
        results.push({ companyId: null, result });
      }

      console.log("✅ Payroll processed successfully:", results);

      const totalUpdated = results.reduce(
        (sum, r) => sum + (r.result?.data?.payrollsUpdated ?? 0), 0,
      );
      const successMessage = t("payrollProcessedSuccess").replace(
        "{count}", totalUpdated || selectedEmployees.length,
      );
      showAlert(t("success"), ` ${successMessage}`, "success");

      onClose();
      setTimeout(() => onProcessComplete(), 500);
    } catch (error) {
      console.error("❌ Error processing payroll:", error);
      const errorMessage = t("payrollProcessingError").replace("{error}", error.message);
      showAlert(t("error"), `❌ ${errorMessage}`, "error");
    } finally {
      setProcessingPayroll(false);
      setProcessingStatus("");
    }
  };

  return {
    companyConfig,
    cutoffDay,
    companies,
    companyConfigsById,
    companiesLoading,
    companiesError,
    formData,
    setFormData,
    previewData,
    setPreviewData,
    selectedAll,
    setSelectedAll,
    processingPayroll,
    processingStatus,
    groupByMode,
    setGroupByMode,
    handleInputChange,
    handleEmployeeSelection,
    handleSelectAll,
    calculatePreview,
    handleProcessPayroll,
  };
};

export default useProcessPayroll;